<template>
  <!-- Écran de chargement initial -->
  <div v-if="isInitializing" class="loading-screen">
    <div class="d-flex flex-column align-items-center justify-content-center" style="height: 100vh">
      <div
        class="spinner-border mb-3"
        role="status"
        style="width: 3rem; height: 3rem; color: var(--c-blue-secondary)"
      >
        <span class="visually-hidden">Chargement...</span>
      </div>
      <p class="text-white fs-5">Vérification de la session...</p>
    </div>
  </div>

  <!-- Application principale -->
  <template v-else>
    <SideBarBlock v-if="showSidebar" class="side-block position-fixed top-0 start-0" />

    <BottomNavBar v-if="showFootBar" class="foot-bar-block" />

    <main
      id="body-block"
      role="main"
      aria-label="Contenu principal"
      class="p-4 pb-0"
      :class="[
        {
          'element-width-according-sidebar': showSidebar && !isMobile,
          'element-according-foot-bar': showFootBar
        }
      ]"
    >
      <router-view id="content_page" v-slot="{ Component }">
        <transition name="slide-fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>

      <div
        v-if="currentRoute !== 'login' && currentRoute !== 'compte' && showFootBar"
        id="btn-add-new-depense"
        class="position-fixed bottom-0 end-0 me-3 mb-3"
        :class="{
          'mb-4': showFootBar
        }"
      >
        <button
          class="btn rounded-5 glass-btn-success"
          :class="{
            'mb-5': showFootBar
          }"
          style="height: 50px; width: 50px; padding: 0px !important"
          aria-label="Ajouter un nouveau revenu ou dépense"
          @click="openModalAddContent"
        >
          <font-awesome-icon icon="fa-solid fa-plus" size="xl" />
        </button>
      </div>

      <form-add-content v-if="modalAddContent.show" @close="closeModalAddContent" />

      <onboarding-prompt-modal
        v-if="showOnboardingPrompt"
        @skip="skipOnboarding"
        @start="startOnboardingNow"
      />
    </main>
  </template>
</template>

<script>
// Store
import useUserStore from '@/stores/user'
import modalAddContentStore from '@/stores/components/modalAddContent'
import useCategorieDepenseStore from '@/stores/categorieDepense'

// Components
import SideBarBlock from '@/components/SideBarBlock.vue'
import BottomNavBar from '@/components/BottomNavBar.vue'
import FormAddContent from '@/components/FormAddContent.vue'
import OnboardingPromptModal from '@/components/Onboarding/OnboardingPromptModal.vue'

// Services
import startOnboardingTour from '@/services/onboardingTour'

export default {
  name: 'App',

  components: {
    SideBarBlock,
    BottomNavBar,
    FormAddContent,
    OnboardingPromptModal
  },

  data() {
    return {
      isMobile: false,
      isPortrait: false,
      isInitializing: true,
      showOnboardingPrompt: false,
      onboardingChecked: false
    }
  },

  setup() {
    const userStore = useUserStore()
    const modalAddContent = modalAddContentStore()
    const categorieDepenseStore = useCategorieDepenseStore()
    return { userStore, modalAddContent, categorieDepenseStore }
  },

  async mounted() {
    // Initial Mobile Check
    this.mediaQuery = window.matchMedia('(max-width: 1024px)')
    this.mediaQuery.addEventListener('change', this.handleMediaQueryChange)
    this.handleMediaQueryChange(this.mediaQuery)

    // Initial Orientation Check
    this.orientationQuery = window.matchMedia('(orientation: portrait)')
    this.orientationQuery.addEventListener('change', this.handleOrientationChange)
    this.handleOrientationChange(this.orientationQuery)

    // Vérifier si on a un refresh token au démarrage
    if (this.userStore.refreshToken && !this.userStore.isLogged) {
      try {
        await this.userStore.askNewToken(this.userStore.refreshToken)

        // Vérifier s'il y a une route sauvegardée
        const afterLoginRoute = window.localStorage.getItem('afterLoginRoute')
        if (afterLoginRoute) {
          const savedRoute = JSON.parse(afterLoginRoute)
          window.localStorage.removeItem('afterLoginRoute')
          await this.$router.push(savedRoute)
        } else if (this.$route.name === 'login') {
          // Si on est sur login et pas de route sauvegardée, aller à home
          await this.$router.push({ name: 'home' })
        }
      } catch (error) {
        console.error('Failed to refresh token on mount:', error)
        // Si le refresh échoue, nettoyer les tokens
        this.userStore.token = null
        this.userStore.refreshToken = null

        // Rediriger vers login si nécessaire
        if (this.$route.meta.mustBeAuthenticated) {
          await this.$router.push({ name: 'login' })
        }
      } finally {
        // Masquer l'écran de chargement après le refresh
        this.isInitializing = false
      }
    } else {
      // Pas de refresh token nécessaire, masquer immédiatement l'écran de chargement
      this.isInitializing = false

      if (!this.userStore.isLogged && this.$route.meta.mustBeAuthenticated) {
        // Pas de refresh token et route protégée : rediriger vers login
        await this.$router.push({ name: 'login' })
      }
    }

    this.checkFirstLoginOnboarding()
  },

  beforeUnmount() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleMediaQueryChange)
    }
    if (this.orientationQuery) {
      this.orientationQuery.removeEventListener('change', this.handleOrientationChange)
    }
  },

  methods: {
    handleMediaQueryChange(e) {
      this.isMobile = e.matches
    },

    handleOrientationChange(e) {
      this.isPortrait = e.matches
    },

    openModalAddContent() {
      // Si on est sur la page de détails d'une catégorie, pré-sélectionner cette catégorie
      if (this.$route.name === 'details-categorie' && this.$route.params.id) {
        this.modalAddContent.preselectedCategorieId = parseInt(this.$route.params.id)
      }
      this.modalAddContent.show = true
    },

    closeModalAddContent() {
      this.modalAddContent.show = false
      // Réinitialiser la catégorie pré-sélectionnée
      this.modalAddContent.resetPreselectedCategorie()
    },

    checkFirstLoginOnboarding() {
      if (this.onboardingChecked) return
      if (this.$route.name !== 'home') return

      this.onboardingChecked = true
      if (!window.localStorage.getItem('hasSeenOnboarding')) {
        this.showOnboardingPrompt = true
      }
    },

    skipOnboarding() {
      window.localStorage.setItem('hasSeenOnboarding', 'true')
      this.showOnboardingPrompt = false
    },

    startOnboardingNow() {
      window.localStorage.setItem('hasSeenOnboarding', 'true')
      this.showOnboardingPrompt = false
      startOnboardingTour({
        modalAddContentStore: this.modalAddContent,
        categorieDepenseStore: this.categorieDepenseStore,
        router: this.$router
      })
    }
  },

  watch: {
    '$route.name'(newName) {
      if (newName === 'home') this.checkFirstLoginOnboarding()
    }
  },

  computed: {
    currentRoute() {
      return this.$route.name
    },
    showFootBar() {
      return this.currentRoute !== 'login' && (this.isMobile || this.isPortrait)
    },
    showSidebar() {
      return this.currentRoute !== 'login' && !this.isMobile && !this.isPortrait
    }
  }
}
</script>

<style>
/* Styles pour le contenu */

.content {
  transition: margin-left 0.5s;

  overflow: auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/*

Enter and leave animations can use different
durations and timing functions.

*/

.slide-fade-enter-active {
  transition: all 0.2s ease-out;

  overflow: hidden !important;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);

  overflow: hidden !important;
}

.slide-fade-enter-from {
  transform: translateX(20px);

  opacity: 0;

  overflow: hidden !important;
}

.slide-fade-leave-to {
  transform: translateX(-20px);

  opacity: 0;

  overflow: hidden !important;
}

.slide-fade-reverse-enter-active {
  transition: all 0.2s ease-out;
  overflow: hidden !important;
}

.slide-fade-reverse-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
  overflow: hidden !important;
}

.slide-fade-reverse-enter-from {
  transform: translateX(-20px);
  opacity: 0;
  overflow: hidden !important;
}

.slide-fade-reverse-leave-to {
  transform: translateX(20px);
  opacity: 0;
  overflow: hidden !important;
}

/* Loading screen */
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, var(--c-blue-primary) 0%, var(--c-blue-secondary) 100%);
  z-index: 9999;
}
</style>
